<template>
  <FocusTrap
    :initial-focus="initialFocusableItem"
    active
  >
    <div
      :style="cssVars"
      class="contact-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="staff-contact-modal-title"
    >
      <div class="contact-modal__overlay">
        <div
          ref="contactModal"
          class="contact-modal__container"
        >
          <!-- Header -->
          <header class="contact-modal__header">
            <button
              :aria-label="$t('pages.staff.contactForm.closeLabel')"
              class="close-btn-container"
              @click="$emit('closeModal')"
            >
              <ModalCloseIcon />
            </button>
            <div class="title-wrapper">
              <h4
                id="staff-contact-modal-title"
                class="title"
              >
                {{ $t('pages.staff.sendMessageTo', { person: member.full_name }) }}
              </h4>
            </div>
          </header>

          <!-- Body -->
          <div class="contact-modal__body">
            <form
              v-if="reqInitial"
              ref="form"
              @submit.prevent=""
            >
              <template
                v-for="field of FIELDS"
                :key="field.id"
              >
                <FormGroup
                  :id="field.id"
                  :type="field.type"
                  :errors="errors[field.id]"
                  :label="field.labelKey && $t(`pages.staff.contactForm.${field.labelKey}`)"
                  :placeholder="field.placeholderKey && $t(`pages.staff.contactForm.${field.placeholderKey}`)"
                  :autocomplete="field.autocomplete"
                  :model-value="$data[field.id]"
                  @update:model-value="handleChange(field.id, $event)"
                />
              </template>
            </form>
            <div v-if="reqLoading">
              {{ $t('pages.shared.loading') }}
            </div>
            <ModalContentDialog
              v-if="showSuccess || showError"
              :is-success="showSuccess"
              :recipient="member.full_name"
            />
          </div>

          <!-- Footer -->
          <footer class="contact-modal__footer">
            <template v-if="reqInitial">
              <FormButton
                variant="primary"
                :color-overrides="submitEnabled ? buttonColorOverrides : {}"
                :disabled="!submitEnabled"
                @click="handleSubmit"
                @keypress.enter="handleSubmit"
              >
                {{ $t('pages.staff.sendMessage') }}
              </FormButton>
              <FormButton
                variant="secondary"
                :color-overrides="buttonColorOverrides"
                @click="handleClose"
              >
                {{ $t('pages.shared.cancel') }}
              </FormButton>
            </template>
            <FormButton
              v-if="showSuccess"
              variant="primary"
              :color-overrides="buttonColorOverrides"
              @click="handleClose"
            >
              {{ $t('pages.shared.close') }}
            </FormButton>
            <template v-if="showError">
              <FormButton
                variant="primary"
                :color-overrides="buttonColorOverrides"
                @click="resetForm(true)"
                @keypress.enter="resetForm(true)"
              >
                {{ $t('pages.staff.tryAgain') }}
              </FormButton>
              <FormButton @click="handleClose">
                {{ $t('pages.shared.cancel') }}
              </FormButton>
            </template>
          </footer>
        </div>
      </div>
    </div>
  </FocusTrap>
</template>

<script>
import { FocusTrap } from 'focus-trap-vue';
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';
import { useReCaptcha } from 'vue-recaptcha-v3';
import ModalCloseIcon from '../../../global/svg/ModalCloseIcon.vue';
import ModalContentDialog from './components/ModalContentDialog';
import FormGroup from '../../../global/Forms/FormGroup.vue';
import FormButton from '../../../global/Forms/FormButton';
import { FIELDS } from '../../../global/constants/Staff.constants';

export default {
  name: 'StaffContactModal',
  components: {
    ModalContentDialog,
    FormButton,
    FormGroup,
    FocusTrap,
    ModalCloseIcon,
  },
  props: {
    member: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const { executeRecaptcha, recaptchaLoaded, instance } = useReCaptcha();

    onBeforeMount(() => {
      instance.value.showBadge();
    });
    onBeforeUnmount(() => {
      instance.value.hideBadge();
    });
    const recaptcha = async () => {
      // (optional) Wait until recaptcha has been loaded.
      await recaptchaLoaded();

      // Execute reCAPTCHA with action "login".
      const token = await executeRecaptcha('staff_email');

      // Do stuff with the received token.
      return token;
    };
    return {
      v$: useVuelidate(),
      recaptcha,
    };
  },
  data() {
    return {
      FIELDS,
      errors: {},
      email: '',
      fullName: '',
      message: '',
    };
  },
  validations: {
    email: {
      required,
      email,
    },
    fullName: {
      required,
    },
    message: {
      required,
    },
  },
  computed: {
    ...mapGetters({
      headingColor: 'getThemeHeadingColor',
      overlayColor: 'getThemeAlertShadowOverlay',
      reqInitial: 'staff/getIsReqInitial',
      reqLoading: 'staff/getIsReqLoading',
      showSuccess: 'staff/getIsReqSuccess',
      showError: 'staff/getIsReqError',
    }),
    buttonColorOverrides() {
      const color = this.headingColor;
      if (!color) {
        return {};
      }

      return {
        '--button-outline': color,
        // Primary
        '--button-primary-bg': color,
        '--button-primary-bg-hover': color,
        // Secondary
        '--button-secondary-bg': `${color}33`,
        '--button-secondary-bg-hover': `${color}4d`,
        '--button-secondary-text': color,
      };
    },
    cssVars() {
      return {
        '--overlay-color': this.overlayColor,
      };
    },
    submitEnabled() {
      return this.email.length && this.fullName.length && this.message.length;
    },
  },
  mounted() {
    window.addEventListener('keydown', this.onKeydown);
    const label = document.createElement('label');
    label.textContent = 'Google Recaptcha';
    label.setAttribute('for', 'g-recaptcha-response');
    label.classList.add('sr-only');
    document.body.appendChild(label);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    onKeydown(e) {
      if (e.key === 'Escape') {
        this.handleClose();
      }
    },
    handleChange(inputName, newValue) {
      this[inputName] = newValue;
    },
    async handleSubmit() {
      this.errors = {};

      this.v$.$touch();
      if (this.v$.$error) {
        this.fetchErrors();
        return;
      }

      let recaptchaToken = '';
      try {
        recaptchaToken = await this.recaptcha();
      } catch (e) {
        console.error(e);
        return;
      }

      const { fullName, message } = this;
      this.$store.dispatch('staff/sendContactEmail', {
        formData: {
          fullName,
          email: this.email,
          message,
          recaptcha_response: recaptchaToken,
          member: this.member,
        },
        writableApi: useRuntimeConfig().public.WRITABLE_BASE_API_URL,
      });
    },
    fetchErrors() {
      this.errors = this.FIELDS.reduce((errors, { id, validators }) => {
        if (!this.v$[id].$error) {
          return errors;
        }

        return {
          ...errors,
          [id]: validators.reduce(
            (accum, validator) => ({
              [validator]: this.v$[id][validator],
            }),
            {},
          ),
        };
      }, {});

      if (this.errors['email']['email'].$invalid) {
        this.$el.querySelector('#email')?.focus();
      }
    },
    handleClose() {
      this.resetForm();
      this.$emit('closeModal');
    },
    resetForm(isRetry) {
      this.$store.commit('staff/initializeReqStatus');

      if (!isRetry) {
        this.fullName = '';
        this.email = '';
        this.message = '';
      }
    },
    initialFocusableItem() {
      return this.$el.getElementsByTagName('input')?.[0];
    },
  },
};
</script>

<style scoped lang="scss">
$primary-dark: #333333;

.contact-modal {
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-content: center;
  align-items: center;

  &__overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    background: var(--overlay-color);
    z-index: 99;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  &__container {
    min-width: 600px;
    max-height: calc(100vh - 1rem);
    background: #ffffff;
    z-index: 100;
    padding: 24px 40px;

    display: flex;
    flex-direction: column;

    @media only screen and (max-width: 599px) {
      min-width: unset;
      min-height: unset;
      padding: 1rem;
    }

    & .contact-modal {
      &__header {
        flex: 1;
        margin-bottom: 24px;
        .title-wrapper {
          .title {
            font-style: normal;
            font-weight: bold;
            font-size: 20px;
            line-height: 24px;
            margin-right: 24px;
            letter-spacing: 0.8px;
            color: $primary-dark;
          }
        }
        .close-btn-container {
          justify-content: flex-end;
          margin-bottom: -24px;
          width: min-content;
          float: right;
          outline: none;
          background: none;
          border: none;
          &:focus {
            outline: -webkit-focus-ring-color auto 1px;
          }
        }
      }
      &__body {
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        overflow: auto;
      }
      &__footer {
        flex: 1;
        margin-top: 24px;
      }
    }
  }
}
</style>
